import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Pencil, Trash2, Truck, Loader2, Printer, X, Mail, Download, Boxes, Factory, DollarSign, Plus } from "lucide-react";
import { printDeliveryOrder, downloadDeliveryOrderPdf } from "@/lib/deliveryOrderPrint";
import { Link } from "react-router-dom";

const num = (n, d = 0) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d });
const fmtLong = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export const DO_STATUS_META = {
  open: { label: "Open", cls: "bg-slate-100 text-slate-600" },
  scheduled: { label: "Scheduled", cls: "bg-sky-50 text-sky-700" },
  ready: { label: "Ready to Ship", pickupLabel: "Ready for Pickup", cls: "bg-violet-50 text-violet-700" },
  dispatched: { label: "In Transit", pickupLabel: "Picked Up", cls: "bg-orange-50 text-orange-700" },
  completed: { label: "Delivered", pickupLabel: "Picked Up", cls: "bg-emerald-50 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-400" },
};

export const doStatusMeta = (status, isPickup) => {
  const m = DO_STATUS_META[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
  return { ...m, label: isPickup && m.pickupLabel ? m.pickupLabel : m.label };
};

// Pickup shipments skip "dispatched": Picked Up maps straight to internal "completed".
export const doStatusOptions = (isPickup) =>
  Object.entries(DO_STATUS_META)
    .filter(([k]) => !(isPickup && k === "dispatched"))
    .map(([k, m]) => [k, isPickup && m.pickupLabel ? m.pickupLabel : m.label]);

const cfgOf = (products, l) => (products.find((p) => p.id === l.product_id)?.pack_configs || []).find((c) => c.id === l.config_id);

const toCases = (l, qty, unit) => {
  const q = Number(qty) || 0;
  if (unit === "units") return l.case_pack ? Math.round((q / l.case_pack) * 100) / 100 : 0;
  if (unit === "pallets") return l.cases_per_pallet ? q * l.cases_per_pallet : 0;
  if (unit === "truckloads") return l.cases_per_pallet && l.pallets_per_truckload ? q * l.cases_per_pallet * l.pallets_per_truckload : 0;
  return q;
};

const lineWeight = (l, cases) => {
  const pallets = l.cases_per_pallet ? cases / l.cases_per_pallet : null;
  if (l.weight_per_pallet && pallets != null) return pallets * l.weight_per_pallet;
  if (l.weight_per_case) return cases * l.weight_per_case;
  return null;
};

export function DODialog({ open, onOpenChange, so, customer = null, existing, products, customers = [], initialLines = null, onSaved }) {
  const [pickupDate, setPickupDate] = useState("");
  const [deliveredDate, setDeliveredDate] = useState("");
  const [carrier, setCarrier] = useState("");
  const [truckNumber, setTruckNumber] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [scac, setScac] = useState("");
  const [nmfcClass, setNmfcClass] = useState("");
  const [rdd, setRdd] = useState("");
  const [shipFrom, setShipFrom] = useState("");
  const [shipToOverride, setShipToOverride] = useState("");
  const [trailerNumber, setTrailerNumber] = useState("");
  const [trackerId, setTrackerId] = useState("");
  const [freightTerms, setFreightTerms] = useState("none");
  const [apptDate, setApptDate] = useState("");
  const [apptTime, setApptTime] = useState("");
  const [physicalPallets, setPhysicalPallets] = useState("");
  const [loadedBy, setLoadedBy] = useState("none");
  const [countedBy, setCountedBy] = useState("none");
  const [notes, setNotes] = useState("");
  const [weightOverride, setWeightOverride] = useState("");
  const [status, setStatus] = useState("open");
  const [shipAddressId, setShipAddressId] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  const shipCustomer = useMemo(() => customers.find((c) => c.id === (existing?.customer_id || so?.customer_id)) || null, [customers, existing, so]);
  const shipAddrs = shipCustomer?.addresses || [];

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setPickupDate(existing.pickup_date || "");
      setDeliveredDate(existing.delivered_date || "");
      setCarrier(existing.carrier || "");
      setTruckNumber(existing.truck_number || "");
      setTrackingNumber(existing.tracking_number || "");
      setSealNumber(existing.seal_number || "");
      setDriverName(existing.driver_name || "");
      setScac(existing.scac || "");
      setNmfcClass(existing.nmfc_class || "");
      setTrailerNumber(existing.trailer_number || "");
      setTrackerId(existing.tracker_id || "");
      setFreightTerms(existing.freight_terms || "none");
      setApptDate(existing.delivery_appointment_date || "");
      setApptTime(existing.delivery_appointment_time || "");
      setPhysicalPallets(existing.physical_pallets != null ? String(existing.physical_pallets) : "");
      setLoadedBy(existing.trailer_loaded_by || "none");
      setCountedBy(existing.freight_counted_by || "none");
      setNotes(existing.notes || "");
      setWeightOverride(existing.total_weight_lbs != null ? String(existing.total_weight_lbs) : "");
      setStatus(existing.status || "open");
      setRdd(existing.rdd || "");
      setShipFrom(existing.ship_from || "");
      setShipToOverride(existing.ship_to_override || "");
      setLines(existing.lines.map((l) => ({ ...l, cases: String(l.cases), ordered: null })));
    } else if (so || customer) {
      setPickupDate(so?.delivery_date || so?.ship_date || "");
      setRdd(so?.ship_date || "");
      setShipFrom("");
      setShipToOverride("");
      setDeliveredDate("");
      setCarrier("");
      setTruckNumber("");
      setTrackingNumber("");
      setSealNumber("");
      setDriverName("");
      setScac("");
      setTrailerNumber("");
      setTrackerId("");
      setFreightTerms("none");
      setApptDate("");
      setApptTime("");
      setPhysicalPallets("");
      setLoadedBy("none");
      setCountedBy("none");
      setNotes("");
      setWeightOverride("");
      setStatus("open");
      setLines(so ? (so.lines || []).map((l) => {
        const cfg = cfgOf(products, l);
        const dims = [cfg?.pallet_length, cfg?.pallet_width, cfg?.pallet_height];
        const pallet_dims = dims.some((d) => Number(d) > 0) ? dims.map((d) => (Number(d) > 0 ? Number(d).toLocaleString() : "?")).join(" × ") + " in" : "";
        return {
          so_line_id: l.id, product_id: l.product_id, product_name: l.product_name,
          design_id: l.design_id || "", design_name: l.design_name || "",
          config_id: l.config_id || "", case_pack: l.case_pack,
          cases: String(l.cases), ordered: l.cases,
          cases_per_pallet: l.cases_per_pallet || Number(cfg?.cases_per_pallet) || null,
          pallets_per_truckload: Number(cfg?.pallets_per_truckload) || null,
          weight_per_case: Number(cfg?.weight_per_case) || null,
          weight_per_pallet: Number(cfg?.weight_per_pallet) || null,
          pallet_dims,
        };
      }) : (initialLines || []));
    }
    const addrs = (customers.find((c) => c.id === (existing?.customer_id || so?.customer_id || customer?.id))?.addresses) || [];
    const cur = existing?.ship_address_id || "";
    setShipAddressId(addrs.find((a) => a.id === cur) ? cur : addrs.length === 1 ? addrs[0].id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, so, customer, products, customers]);

  const totals = useMemo(() => {
    let cases = 0, pallets = 0, weight = 0;
    lines.forEach((l) => {
      const c = Number(l.cases) || 0;
      cases += c;
      if (l.cases_per_pallet) pallets += c / l.cases_per_pallet;
      weight += lineWeight(l, c) || 0;
    });
    return { cases, pallets, weight };
  }, [lines]);

  const valid = lines.some((l) => Number(l.cases) > 0);

  const [addProdId, setAddProdId] = useState("");
  const [addCfgId, setAddCfgId] = useState("");
  const [addDesignId, setAddDesignId] = useState("");
  const addProd = products.find((p) => p.id === addProdId);
  const addDesigns = (addProd?.lines || []).flatMap((l) => l.designs || []);
  const addLine = () => {
    const cfg = (addProd?.pack_configs || []).find((c) => c.id === addCfgId) || (addProd?.pack_configs || [])[0];
    if (!addProd || !cfg) { toast.error("Pick a product (and pack) first"); return; }
    const d = addDesigns.find((x) => x.id === addDesignId);
    const dims = [cfg.pallet_length, cfg.pallet_width, cfg.pallet_height];
    const pallet_dims = dims.some((v) => Number(v) > 0) ? dims.map((v) => (Number(v) > 0 ? Number(v).toLocaleString() : "?")).join(" × ") + " in" : "";
    setLines((prev) => [...prev, {
      so_line_id: "", product_id: addProd.id, product_name: addProd.name,
      design_id: d?.id || "", design_name: d?.name || "",
      config_id: cfg.id || "", case_pack: Number(cfg.bottles_per_case) || 0,
      cases: "0", ordered: null,
      cases_per_pallet: Number(cfg.cases_per_pallet) || null,
      pallets_per_truckload: Number(cfg.pallets_per_truckload) || null,
      weight_per_case: Number(cfg.weight_per_case) || null,
      weight_per_pallet: Number(cfg.weight_per_pallet) || null,
      pallet_dims,
    }]);
    setAddProdId(""); setAddCfgId(""); setAddDesignId("");
  };

  const save = async () => {
    if (!valid) return;
    if (shipAddrs.length > 1 && !shipAddressId) { toast.error("This customer has multiple addresses — select a delivery address for the BOL"); return; }
    setSaving(true);
    try {
      const payload = {
        so_id: existing?.so_id || so?.id, customer_id: customer?.id || "", status,
        ship_address_id: shipAddressId,
        pickup_date: pickupDate, delivered_date: deliveredDate,
        carrier, truck_number: truckNumber, tracking_number: trackingNumber, seal_number: sealNumber, notes,
        driver_name: driverName, scac, nmfc_class: nmfcClass, trailer_number: trailerNumber, tracker_id: trackerId,
        rdd, ship_from: shipFrom, ship_to_override: shipToOverride,
        freight_terms: freightTerms === "none" ? "" : freightTerms,
        delivery_appointment_date: apptDate, delivery_appointment_time: apptTime,
        physical_pallets: physicalPallets !== "" ? Number(physicalPallets) : null,
        trailer_loaded_by: loadedBy === "none" ? "" : loadedBy,
        freight_counted_by: countedBy === "none" ? "" : countedBy,
        total_weight_lbs: weightOverride ? Number(weightOverride) : null,
        lines: lines.map((l) => ({
          so_line_id: l.so_line_id || "", product_id: l.product_id, product_name: l.product_name,
          design_id: l.design_id || "", design_name: l.design_name || "", config_id: l.config_id || "",
          case_pack: l.case_pack, cases: Number(l.cases) || 0,
          cases_per_pallet: l.cases_per_pallet, weight_per_case: l.weight_per_case,
          weight_per_pallet: l.weight_per_pallet || null, pallet_dims: l.pallet_dims || "",
        })),
      };
      const { data } = existing
        ? await api.put(`/delivery-orders/${existing.id}`, payload)
        : await api.post("/delivery-orders", payload);
      toast.success(existing ? `${data.do_number} updated` : `${data.do_number} created for ${data.so_number || data.customer_name}`);
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save delivery order");
    } finally {
      setSaving(false);
    }
  };

  const header = existing ? `Edit ${existing.do_number}` : `New Delivery Order — ${so?.so_number || customer?.company_name || ""}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-3xl" data-testid="do-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{header}</DialogTitle>
          <DialogDescription>Factory to {existing?.customer_name || so?.customer_name || customer?.company_name || "customer"} — {so || existing?.so_id ? "deliver all or part of the sales order." : "standalone shipment (no sales order)."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Pickup Date</Label>
              <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className="rounded-sm" data-testid="do-pickup-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Delivery Date</Label>
              <Input type="date" value={rdd} onChange={(e) => setRdd(e.target.value)} className="rounded-sm" data-testid="do-rdd" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Carrier</Label>
              <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier name" className="rounded-sm" data-testid="do-carrier" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Tractor / Truck #</Label>
              <Input value={truckNumber} onChange={(e) => setTruckNumber(e.target.value)} placeholder="Optional" className="rounded-sm" data-testid="do-truck" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">PRO / Tracking #</Label>
              <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="Optional" className="rounded-sm" data-testid="do-tracking" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Driver Name</Label>
              <Input value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="Optional" className="rounded-sm" data-testid="do-driver" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">SCAC</Label>
              <Input value={scac} onChange={(e) => setScac(e.target.value)} placeholder="Optional" className="rounded-sm" data-testid="do-scac" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">NMFC Class</Label>
              <Input value={nmfcClass} onChange={(e) => setNmfcClass(e.target.value)} placeholder="e.g. 55" className="rounded-sm" data-testid="do-nmfc-class" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Trailer #</Label>
              <Input value={trailerNumber} onChange={(e) => setTrailerNumber(e.target.value)} placeholder="Optional" className="rounded-sm" data-testid="do-trailer" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Seal #</Label>
              <Input value={sealNumber} onChange={(e) => setSealNumber(e.target.value)} placeholder="Trailer seal" className="rounded-sm" data-testid="do-seal" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Tracker ID #</Label>
              <Input value={trackerId} onChange={(e) => setTrackerId(e.target.value)} placeholder="Optional" className="rounded-sm" data-testid="do-tracker-id" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Freight Terms</Label>
              <Select value={freightTerms} onValueChange={setFreightTerms}>
                <SelectTrigger className="rounded-sm" data-testid="do-freight-terms"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="prepaid">Prepaid</SelectItem>
                  <SelectItem value="collect">Collect</SelectItem>
                  <SelectItem value="third_party">Third Party</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Delivery Appt Date</Label>
              <Input type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} className="rounded-sm" data-testid="do-appt-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Delivery Appt Time</Label>
              <Input value={apptTime} onChange={(e) => setApptTime(e.target.value)} placeholder="e.g. 10:00 AM" className="rounded-sm" data-testid="do-appt-time" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Trailer Loaded By</Label>
              <Select value={loadedBy} onValueChange={setLoadedBy}>
                <SelectTrigger className="rounded-sm" data-testid="do-loaded-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="shipper">Shipper</SelectItem>
                  <SelectItem value="driver">Driver</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Freight Counted By</Label>
              <Select value={countedBy} onValueChange={setCountedBy}>
                <SelectTrigger className="rounded-sm" data-testid="do-counted-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="shipper">Shipper</SelectItem>
                  <SelectItem value="driver">Driver</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {shipAddrs.length > 1 ? (
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Delivery Address <span className="text-red-600">*</span></Label>
              <Select value={shipAddressId || "__none"} onValueChange={(v) => setShipAddressId(v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="do-ship-address"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Select delivery address…</SelectItem>
                  {shipAddrs.map((a) => (<SelectItem key={a.id} value={a.id} data-testid={`do-ship-address-option-${a.id}`}>{a.label ? `${a.label} — ` : ""}{a.text.split("\n")[0]}</SelectItem>))}
                </SelectContent>
              </Select>
              {shipAddressId ? (
                <p className="whitespace-pre-line text-xs text-slate-500" data-testid="do-ship-address-preview">{shipAddrs.find((a) => a.id === shipAddressId)?.text}</p>
              ) : (
                <p className="text-xs font-medium text-red-600" data-testid="do-ship-address-required">Required — this customer has {shipAddrs.length} addresses on file.</p>
              )}
            </div>
          ) : shipAddrs.length === 1 ? (
            <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Delivery Address (synced from contact)</p>
              <p className="whitespace-pre-line text-xs text-slate-600" data-testid="do-ship-address-single">{shipAddrs[0].label ? `${shipAddrs[0].label}: ` : ""}{shipAddrs[0].text}</p>
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Ship From (BOL)</Label>
              <Textarea value={shipFrom} onChange={(e) => setShipFrom(e.target.value)} rows={2}
                placeholder={"Leave blank to use your company address\nName, street, city, state ZIP"}
                className="rounded-sm text-sm" data-testid="do-ship-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Ship To Override (BOL)</Label>
              <Textarea value={shipToOverride} onChange={(e) => setShipToOverride(e.target.value)} rows={2}
                placeholder={"Leave blank to use the delivery address above"}
                className="rounded-sm text-sm" data-testid="do-ship-to-override" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Lines to Deliver</Label>
            <div className="border border-slate-200">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <th className="px-3 py-1.5">Product</th>
                    <th className="w-24 px-3 py-1.5 text-right">Ordered</th>
                    <th className="w-48 px-3 py-1.5 text-right">Deliver Qty</th>
                    <th className="w-20 px-3 py-1.5 text-right">Pallets</th>
                    <th className="w-24 px-3 py-1.5 text-right">Weight (lbs)</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => {
                    const c = Number(l.cases) || 0;
                    return (
                      <tr key={`${l.so_line_id || l.product_id || "row"}-${i}`} className="border-b border-slate-100">
                        <td className="px-3 py-1.5">
                          <p className="text-[13px] font-medium leading-tight text-slate-800">{l.product_name}</p>
                          <p className="text-[11px] leading-tight text-slate-500">{l.design_name ? `${l.design_name} · ` : ""}{Math.round(l.case_pack)}pk</p>
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-400">{l.ordered != null ? num(l.ordered, 2) : "—"}</td>
                        <td className="px-3 py-1.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Input type="text" inputMode="decimal" value={l.qty != null ? l.qty : l.cases}
                              onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setLines((p) => p.map((x, j) => (j === i ? { ...x, qty: v, cases: String(toCases(x, v, x.unit || "cases")) } : x))); }}
                              className="h-8 w-16 rounded-sm px-1.5 text-right tabular-nums" data-testid={`do-line-cases-${i}`} />
                            <Select value={l.unit || "cases"} onValueChange={(u) => setLines((p) => p.map((x, j) => (j === i ? { ...x, unit: u, cases: String(toCases(x, x.qty != null ? x.qty : x.cases, u)) } : x)))}>
                              <SelectTrigger className="h-8 w-[100px] rounded-sm px-1.5 text-xs" data-testid={`do-line-unit-${i}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="truckloads">Truckloads</SelectItem>
                                <SelectItem value="pallets">Pallets</SelectItem>
                                <SelectItem value="cases">Cases</SelectItem>
                                <SelectItem value="units">Units</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {(l.unit || "cases") !== "cases" && <p className="mt-0.5 text-right text-[10px] tabular-nums text-slate-400" data-testid={`do-line-cases-calc-${i}`}>= {num(Number(l.cases) || 0, 2)} cases</p>}
                        </td>
                        <td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-500">{l.cases_per_pallet ? num(c / l.cases_per_pallet, 2) : "—"}</td>
                        <td className="px-3 py-1.5 text-right text-xs tabular-nums text-slate-500">{lineWeight(l, c) != null ? num(lineWeight(l, c), 1) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 text-xs font-semibold text-slate-700">
                    <td className="px-3 py-1.5">Totals</td>
                    <td />
                    <td className="px-3 py-1.5 text-right tabular-nums" data-testid="do-total-cases">{num(totals.cases, 2)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{num(totals.pallets, 2)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{totals.weight ? num(totals.weight, 1) : "—"}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {!existing && !so && (
              <div className="flex flex-wrap items-center gap-2 border border-dashed border-slate-300 p-2" data-testid="do-add-line-row">
                <Select value={addProdId || undefined} onValueChange={(v) => { setAddProdId(v); setAddCfgId(""); setAddDesignId(""); }}>
                  <SelectTrigger className="h-8 w-44 rounded-sm text-xs" data-testid="do-add-product"><SelectValue placeholder="Product…" /></SelectTrigger>
                  <SelectContent>{products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                </Select>
                {(addProd?.pack_configs || []).length > 1 && (
                  <Select value={addCfgId || undefined} onValueChange={setAddCfgId}>
                    <SelectTrigger className="h-8 w-28 rounded-sm text-xs" data-testid="do-add-config"><SelectValue placeholder="Pack…" /></SelectTrigger>
                    <SelectContent>{addProd.pack_configs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${Math.round(c.bottles_per_case)}pk`}</SelectItem>))}</SelectContent>
                  </Select>
                )}
                {addDesigns.length > 0 && (
                  <Select value={addDesignId || undefined} onValueChange={setAddDesignId}>
                    <SelectTrigger className="h-8 w-36 rounded-sm text-xs" data-testid="do-add-design"><SelectValue placeholder="Design…" /></SelectTrigger>
                    <SelectContent>{addDesigns.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
                  </Select>
                )}
                <Button size="sm" variant="outline" onClick={addLine} disabled={!addProdId} className="h-8 rounded-sm text-xs" data-testid="do-add-line-btn">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add product
                </Button>
              </div>
            )}
            <p className="text-[10px] text-slate-400">Set a line to 0 to leave it off this delivery. Weight uses the pack config's Weight per Case when set on the BOM.</p>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-sm" data-testid="do-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DO_STATUS_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {status === "completed" && (
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Delivered Date</Label>
                <Input type="date" value={deliveredDate} onChange={(e) => setDeliveredDate(e.target.value)} className="rounded-sm" data-testid="do-delivered-date" />
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Weight Override (lbs)</Label>
              <Input type="text" inputMode="decimal" value={weightOverride} onChange={(e) => setWeightOverride(e.target.value.replace(/[^0-9.]/g, ""))} placeholder={totals.weight ? num(totals.weight, 1) : "Optional"} className="rounded-sm text-right tabular-nums" data-testid="do-weight-override" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Physical Pallets</Label>
              <Input type="text" inputMode="numeric" value={physicalPallets} onChange={(e) => setPhysicalPallets(e.target.value.replace(/[^0-9]/g, ""))} placeholder={totals.pallets ? String(Math.ceil(totals.pallets)) : "Auto"} className="rounded-sm text-right tabular-nums" data-testid="do-physical-pallets" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Special Instructions / Shipping Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-sm" placeholder="Shipment-specific instructions shown on the BOL…" data-testid="do-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={!valid || saving || (shipAddrs.length > 1 && !shipAddressId)} onClick={save} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="do-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{existing ? "Save Changes" : "Create Delivery Order"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const DO_DIRECTION_META = {
  to_customer: { label: "To Customer", cls: "bg-sky-50 text-sky-700" },
  to_copacker: { label: "To Co-Packer", cls: "bg-violet-50 text-violet-700" },
  from_supplier: { label: "From Supplier", cls: "bg-amber-50 text-amber-700" },
  to_warehouse: { label: "Co-Packer → Warehouse", cls: "bg-emerald-50 text-emerald-700" },
  supplier_to_copacker: { label: "Supplier → Co-Packer", cls: "bg-rose-50 text-rose-700" },
};

export function CoPackerFGDialog({ open, onOpenChange, coPackers = [], customers = [], onSaved, initialDirection = "to_warehouse" }) {
  const [direction, setDirection] = useState("to_warehouse");
  const [partnerId, setPartnerId] = useState("");
  const [soId, setSoId] = useState("");
  const [sos, setSos] = useState([]);
  const [balances, setBalances] = useState([]);
  const [qty, setQty] = useState({});
  const [pickupDate, setPickupDate] = useState("");
  const [status, setStatus] = useState("open");
  const [shipAddressId, setShipAddressId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDirection(initialDirection); setPartnerId(""); setSoId(""); setQty({}); setPickupDate(""); setStatus("open"); setShipAddressId("");
    api.get("/sales-orders").then(({ data }) => setSos((data || []).filter((s) => s.status === "open"))).catch(() => setSos([]));
  }, [open, initialDirection]);
  useEffect(() => {
    if (!partnerId) { setBalances([]); return; }
    api.get("/copacker-fg", { params: { co_packer_id: partnerId } }).then(({ data }) => setBalances(data || [])).catch(() => setBalances([]));
  }, [partnerId]);

  const so = sos.find((s) => s.id === soId) || null;
  const customer = so ? customers.find((c) => c.id === so.customer_id) : null;
  const custAddrs = useMemo(() => customer?.addresses || [], [customer]);
  useEffect(() => {
    setShipAddressId((cur) => (custAddrs.find((a) => a.id === cur) ? cur : custAddrs.length === 1 ? custAddrs[0].id : ""));
  }, [custAddrs]);

  const lbl = "text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500";
  const inputCls = "h-9 rounded-sm";

  const save = async () => {
    if (!partnerId) { toast.error("Pick a co-packer"); return; }
    if (direction === "to_customer" && !soId) { toast.error("Pick the sales order this shipment fulfills"); return; }
    if (direction === "to_customer" && custAddrs.length > 1 && !shipAddressId) { toast.error("Select the customer's delivery address"); return; }
    const lines = balances.filter((b) => Number(qty[b.id]) > 0).map((b) => {
      const soLine = (so?.lines || []).find((l) => l.product_id === b.product_id && (l.design_id || "") === (b.design_id || "") && Number(l.case_pack || 0) === Number(b.case_pack || 0));
      return {
        so_line_id: soLine?.id || "",
        product_id: b.product_id, product_name: b.product_name || "", design_id: b.design_id || "", design_name: b.design_name || "",
        case_pack: b.case_pack || 0, cases: Number(qty[b.id]),
      };
    });
    if (!lines.length) { toast.error("Enter cases for at least one product"); return; }
    setSaving(true);
    try {
      await api.post("/delivery-orders", { kind: "fg_copacker", direction, partner_id: partnerId, so_id: soId, status, pickup_date: pickupDate, ship_address_id: shipAddressId, lines });
      toast.success("Shipment created");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create shipment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="fgcp-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Ship Finished Goods From Co-Packer</DialogTitle>
          <DialogDescription>Moves finished goods off the co-packer's balance — to your warehouse or straight to a customer.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="space-y-1">
              <Label className={lbl}>Destination</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger className={inputCls} data-testid="fgcp-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="to_warehouse">Our Warehouse</SelectItem>
                  <SelectItem value="to_customer">Direct to Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Co-Packer</Label>
              <Select value={partnerId || "__none"} onValueChange={(v) => setPartnerId(v === "__none" ? "" : v)}>
                <SelectTrigger className={inputCls} data-testid="fgcp-partner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Select…</SelectItem>
                  {coPackers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {direction === "to_customer" && (
              <div className="space-y-1">
                <Label className={lbl}>Sales Order</Label>
                <Select value={soId || "__none"} onValueChange={(v) => setSoId(v === "__none" ? "" : v)}>
                  <SelectTrigger className={inputCls} data-testid="fgcp-so"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Select…</SelectItem>
                    {sos.map((s) => (<SelectItem key={s.id} value={s.id}>{s.so_number} — {s.customer_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className={lbl}>Pickup Date</Label>
              <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className={inputCls} data-testid="fgcp-pickup" />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className={inputCls} data-testid="fgcp-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DO_STATUS_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {direction === "to_customer" && custAddrs.length > 1 && (
            <div className="space-y-1">
              <Label className={lbl}>Delivery Address <span className="text-red-600">*</span></Label>
              <Select value={shipAddressId || "__none"} onValueChange={(v) => setShipAddressId(v === "__none" ? "" : v)}>
                <SelectTrigger className={inputCls} data-testid="fgcp-ship-address"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Select delivery address…</SelectItem>
                  {custAddrs.map((a) => (<SelectItem key={a.id} value={a.id}>{a.label ? `${a.label} — ` : ""}{a.text.split("\n")[0]}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="border border-slate-200" data-testid="fgcp-balances">
            <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">Finished Goods at {coPackers.find((c) => c.id === partnerId)?.company_name || "Co-Packer"}</div>
            {!partnerId ? (
              <p className="px-3 py-3 text-xs text-slate-400">Pick a co-packer to see their finished goods balance.</p>
            ) : balances.length === 0 ? (
              <p className="px-3 py-3 text-xs text-slate-400">No finished goods held at this co-packer yet — record ALUM production against them first.</p>
            ) : balances.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0" data-testid={`fgcp-balance-${b.id}`}>
                <span className="text-sm text-slate-800">{b.product_name}{b.design_name ? ` · ${b.design_name}` : ""}{b.case_pack ? ` · ${Math.round(b.case_pack)}-Pack` : ""}
                  <span className="ml-2 text-xs text-slate-400">{Math.round(b.cases).toLocaleString()} CS held</span>
                </span>
                <Input inputMode="numeric" placeholder="0" value={qty[b.id] || ""} onChange={(e) => setQty((p) => ({ ...p, [b.id]: e.target.value.replace(/[^0-9.]/g, "") }))} className="h-8 w-24 rounded-sm text-right tabular-nums" data-testid={`fgcp-qty-${b.id}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={save} disabled={saving} data-testid="fgcp-save">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Truck className="mr-1.5 h-4 w-4" />} Create Shipment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ComponentDODialog({ open, onOpenChange, existing, components, coPackers, suppliers, onSaved, initialDirection = "to_copacker" }) {
  const [direction, setDirection] = useState("to_copacker");
  const [partnerId, setPartnerId] = useState("");
  const [originId, setOriginId] = useState("");
  const [poId, setPoId] = useState("");
  const [pos, setPos] = useState([]);
  const [status, setStatus] = useState("open");
  const [pickupDate, setPickupDate] = useState("");
  const [carrier, setCarrier] = useState("");
  const [truckNumber, setTruckNumber] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [scac, setScac] = useState("");
  const [nmfcClass, setNmfcClass] = useState("");
  const [trailerNumber, setTrailerNumber] = useState("");
  const [trackerId, setTrackerId] = useState("");
  const [freightTerms, setFreightTerms] = useState("none");
  const [physicalPallets, setPhysicalPallets] = useState("");
  const [notes, setNotes] = useState("");
  const [shipAddressId, setShipAddressId] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  const variants = useMemo(() => components.flatMap((c) => (c.types || []).map((t) => {
    const G_PER = { g: 1, mg: 0.001, kg: 1000, oz: 28.3495, lb: 453.592, lbs: 453.592 };
    const unitsPerPallet = t.units_per_pallet || ((t.units_per_case || 0) * (t.cases_per_pallet || 0)) || null;
    const rollsPerPallet = (t.rolls_per_case || 0) * (t.cases_per_pallet || 0) || null;
    const upp = unitsPerPallet || rollsPerPallet;
    let wppLbs = t.weight_per_pallet != null
      ? Math.round((t.pallet_weight_unit === "kg" ? t.weight_per_pallet * 2.20462 : t.weight_per_pallet) * 10) / 10
      : null;
    let wppCalc = false;
    if (wppLbs == null) {
      // No pallet weight on the variant — derive it from unit weight × quantity per pallet
      if (rollsPerPallet && t.roll_gross_weight) {
        const g = G_PER[(t.roll_weight_unit || "lbs").toLowerCase()] || 453.592;
        wppLbs = Math.round(((t.roll_gross_weight * g * rollsPerPallet) / 453.592) * 10) / 10;
        wppCalc = true;
      } else if (unitsPerPallet) {
        const wpu = t.weight_per_unit ?? c.default_weight_per_unit;
        const wUnit = ((t.weight_per_unit != null ? t.weight_unit : c.default_weight_unit) || "g").toLowerCase();
        const g = G_PER[wUnit];
        if (wpu && g) { wppLbs = Math.round(((wpu * g * unitsPerPallet) / 453.592) * 10) / 10; wppCalc = true; }
      }
    }
    return {
      key: `${c.id}|${t.id}`, component_id: c.id, type_id: t.id,
      label: `${c.name} — ${t.name || t.label || ""}`, designs: t.designs || [],
      upp: upp || null, dims: t.pallet_dims || "", wpp: wppLbs, wppCalc,
    };
  })), [components]);
  const vByKey = useMemo(() => Object.fromEntries(variants.map((v) => [v.key, v])), [variants]);
  const emptyLine = () => ({ _k: Math.random().toString(36).slice(2), key: "", design_id: "", units: "", pallets: "", pallet_dims: "", weight_per_pallet: "" });

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setDirection(existing.direction || "to_copacker");
      setPartnerId(existing.partner_id || "");
      setOriginId(existing.origin_partner_id || "");
      setPoId(existing.po_id || "");
      setStatus(existing.status || "open");
      setPickupDate(existing.pickup_date || "");
      setCarrier(existing.carrier || "");
      setTruckNumber(existing.truck_number || "");
      setTrackingNumber(existing.tracking_number || "");
      setSealNumber(existing.seal_number || "");
      setDriverName(existing.driver_name || "");
      setScac(existing.scac || "");
      setNmfcClass(existing.nmfc_class || "");
      setTrailerNumber(existing.trailer_number || "");
      setTrackerId(existing.tracker_id || "");
      setFreightTerms(existing.freight_terms || "none");
      setPhysicalPallets(existing.physical_pallets != null ? String(existing.physical_pallets) : "");
      setNotes(existing.notes || "");
      setLines((existing.lines || []).map((l) => ({
        _k: l.id, key: `${l.component_id}|${l.type_id}`, design_id: l.design_id || "",
        units: String(l.units ?? ""), pallets: l.pallets != null ? String(l.pallets) : "",
        pallet_dims: l.pallet_dims || "", weight_per_pallet: l.weight_per_pallet != null ? String(l.weight_per_pallet) : "",
      })));
    } else {
      setDirection(initialDirection); setPartnerId(""); setStatus("open"); setPickupDate("");
      setOriginId(""); setPoId("");
      setCarrier(""); setTruckNumber(""); setTrackingNumber(""); setSealNumber("");
      setDriverName(""); setScac(""); setTrailerNumber(""); setTrackerId("");
      setFreightTerms("none"); setPhysicalPallets(""); setNotes("");
      setLines([emptyLine()]);
    }
    setShipAddressId(existing?.ship_address_id || "");
  }, [open, existing, initialDirection]);

  useEffect(() => {
    if (!open || direction !== "supplier_to_copacker") return;
    api.get("/purchase-orders").then(({ data }) => setPos(data || [])).catch(() => setPos([]));
  }, [open, direction]);
  const supplierPos = useMemo(() => pos.filter((p) => (!originId || p.supplier_id === originId) && p.status !== "received"), [pos, originId]);

  const partners = direction === "from_supplier" ? suppliers : coPackers;
  const partnerAddrs = useMemo(() => (partners.find((p) => p.id === partnerId)?.addresses) || [], [partners, partnerId]);
  useEffect(() => {
    setShipAddressId((cur) => (partnerAddrs.find((a) => a.id === cur) ? cur : partnerAddrs.length === 1 ? partnerAddrs[0].id : ""));
  }, [partnerAddrs]);
  const setLine = (k, patch) => setLines((prev) => prev.map((l) => (l._k === k ? { ...l, ...patch } : l)));
  const pickVariant = (l, key) => {
    const v = vByKey[key];
    const patch = { key, design_id: "" };
    if (v) {
      patch.pallet_dims = v.dims || "";
      patch.weight_per_pallet = v.wpp != null ? String(v.wpp) : "";
      if (v.upp && Number(l.units) > 0) patch.pallets = String(Math.round((Number(l.units) / v.upp) * 100) / 100);
    }
    setLine(l._k, patch);
  };
  const onUnitsChange = (l, raw) => {
    const val = raw.replace(/[^0-9.]/g, "");
    const upp = vByKey[l.key]?.upp;
    const patch = { units: val };
    if (upp && Number(val) > 0) patch.pallets = String(Math.round((Number(val) / upp) * 100) / 100);
    setLine(l._k, patch);
  };
  const onPalletsChange = (l, raw) => {
    const val = raw.replace(/[^0-9.]/g, "");
    const upp = vByKey[l.key]?.upp;
    const patch = { pallets: val };
    if (upp && Number(val) > 0) patch.units = String(Math.round(Number(val) * upp));
    setLine(l._k, patch);
  };

  const save = async () => {
    if (!partnerId) { toast.error(direction === "from_supplier" ? "Pick a supplier" : "Pick a co-packer"); return; }
    if (direction === "supplier_to_copacker" && !originId) { toast.error("Pick the supplier this shipment comes from"); return; }
    if (partnerAddrs.length > 1 && !shipAddressId) { toast.error("This contact has multiple addresses — select which one to use on the BOL"); return; }
    const component_lines = lines.filter((l) => l.key && Number(l.units) > 0).map((l) => {
      const v = vByKey[l.key];
      const dName = (v?.designs || []).find((d) => d.id === l.design_id)?.name;
      return {
        component_id: v.component_id, type_id: v.type_id, design_id: l.design_id || "",
        name: `${v.label}${dName ? ` (${dName})` : ""}`,
        units: Number(l.units), pallets: l.pallets !== "" ? Number(l.pallets) : null,
        pallet_dims: l.pallet_dims, weight_per_pallet: l.weight_per_pallet !== "" ? Number(l.weight_per_pallet) : null,
      };
    });
    if (!component_lines.length) { toast.error("Add at least one component line with units"); return; }
    setSaving(true);
    try {
      const payload = {
        kind: "component", direction, partner_id: partnerId, status, pickup_date: pickupDate,
        ship_address_id: shipAddressId,
        origin_partner_id: direction === "supplier_to_copacker" ? originId : "",
        po_id: direction === "supplier_to_copacker" && poId ? poId : "",
        carrier, truck_number: truckNumber, tracking_number: trackingNumber, seal_number: sealNumber,
        driver_name: driverName, scac, nmfc_class: nmfcClass, trailer_number: trailerNumber, tracker_id: trackerId,
        freight_terms: freightTerms === "none" ? "" : freightTerms,
        physical_pallets: physicalPallets !== "" ? Number(physicalPallets) : null,
        notes, component_lines,
      };
      const { data } = existing
        ? await api.put(`/delivery-orders/${existing.id}`, payload)
        : await api.post("/delivery-orders", payload);
      toast.success(existing ? `${data.do_number} updated` : `${data.do_number} created`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save shipment");
    } finally { setSaving(false); }
  };

  const inputCls = "rounded-sm";
  const lbl = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-3xl" data-testid="component-do-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{existing ? `Edit ${existing.do_number}` : direction === "supplier_to_copacker" ? "New Supplier → Co-Packer Shipment" : "New Co-Packer Shipment"}</DialogTitle>
          <DialogDescription>
            {direction === "supplier_to_copacker"
              ? "Components ship straight from the supplier to the co-packer. Marking it In Transit credits the co-packer's balance — your warehouse is untouched. Link a PO to log the receipt against it."
              : direction === "from_supplier"
                ? "Inbound components from a supplier — a paper BOL only, inventory is updated when you receive the PO."
                : "Ship components from your inventory. Marking it In Transit moves the stock to the co-packer's balance."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="space-y-1">
              <Label className={lbl}>Direction</Label>
              <Select value={direction} onValueChange={(v) => { setDirection(v); setPartnerId(""); setOriginId(""); setPoId(""); }} disabled={!!existing}>
                <SelectTrigger className={inputCls} data-testid="cdo-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="to_copacker">Warehouse → Co-Packer</SelectItem>
                  <SelectItem value="from_supplier">Supplier → Warehouse</SelectItem>
                  <SelectItem value="supplier_to_copacker">Supplier → Co-Packer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {direction === "supplier_to_copacker" && (
              <div className="space-y-1">
                <Label className={lbl}>Supplier (Ship From)</Label>
                <Select value={originId || "__none"} onValueChange={(v) => { setOriginId(v === "__none" ? "" : v); setPoId(""); }} disabled={!!existing}>
                  <SelectTrigger className={inputCls} data-testid="cdo-origin-supplier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Select…</SelectItem>
                    {suppliers.map((p) => (<SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className={lbl}>{direction === "from_supplier" ? "Supplier" : "Co-Packer"}</Label>
              <Select value={partnerId || "__none"} onValueChange={(v) => setPartnerId(v === "__none" ? "" : v)} disabled={!!existing}>
                <SelectTrigger className={inputCls} data-testid="cdo-partner"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Select…</SelectItem>
                  {partners.map((p) => (<SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Pickup Date</Label>
              <Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className={inputCls} data-testid="cdo-pickup-date" />
            </div>
            <div className="space-y-1">
              <Label className={lbl}>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className={inputCls} data-testid="cdo-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DO_STATUS_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {direction === "supplier_to_copacker" && (
            <div className="space-y-1">
              <Label className={lbl}>Purchase Order (optional)</Label>
              <Select value={poId || "__none"} onValueChange={(v) => setPoId(v === "__none" ? "" : v)} disabled={!!existing}>
                <SelectTrigger className={inputCls} data-testid="cdo-po"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No PO — standalone shipment</SelectItem>
                  {supplierPos.map((p) => (<SelectItem key={p.id} value={p.id}>{p.po_number} — {p.supplier_name} · {Math.round(p.total_ordered - p.total_received).toLocaleString()} remaining</SelectItem>))}
                </SelectContent>
              </Select>
              {poId && <p className="text-xs text-slate-500">Delivered units will be logged as received on this PO (at the co-packer, not your warehouse).</p>}
            </div>
          )}

          {partnerAddrs.length > 1 ? (
            <div className="space-y-1">
              <Label className={lbl}>{direction === "from_supplier" ? "Supplier Address" : "Delivery Address"} <span className="text-red-600">*</span></Label>
              <Select value={shipAddressId || "__none"} onValueChange={(v) => setShipAddressId(v === "__none" ? "" : v)}>
                <SelectTrigger className={inputCls} data-testid="cdo-ship-address"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Select address…</SelectItem>
                  {partnerAddrs.map((a) => (<SelectItem key={a.id} value={a.id} data-testid={`cdo-ship-address-option-${a.id}`}>{a.label ? `${a.label} — ` : ""}{a.text.split("\n")[0]}</SelectItem>))}
                </SelectContent>
              </Select>
              {shipAddressId ? (
                <p className="whitespace-pre-line text-xs text-slate-500" data-testid="cdo-ship-address-preview">{partnerAddrs.find((a) => a.id === shipAddressId)?.text}</p>
              ) : (
                <p className="text-xs font-medium text-red-600" data-testid="cdo-ship-address-required">Required — this contact has {partnerAddrs.length} addresses on file.</p>
              )}
            </div>
          ) : partnerAddrs.length === 1 ? (
            <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">{direction === "from_supplier" ? "Supplier Address" : "Delivery Address"} (synced from contact)</p>
              <p className="whitespace-pre-line text-xs text-slate-600" data-testid="cdo-ship-address-single">{partnerAddrs[0].label ? `${partnerAddrs[0].label}: ` : ""}{partnerAddrs[0].text}</p>
            </div>
          ) : null}

          <div className="border border-slate-200" data-testid="cdo-lines">
            <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">Components to Ship</div>
            {lines.map((l) => {
              const v = vByKey[l.key];
              return (
                <div key={l._k} className="flex flex-wrap items-end gap-2 border-b border-slate-100 px-3 py-2 last:border-b-0">
                  <div className="min-w-[220px] flex-1 space-y-1">
                    <Label className={lbl}>Component</Label>
                    <Select value={l.key || "__none"} onValueChange={(val) => pickVariant(l, val === "__none" ? "" : val)}>
                      <SelectTrigger className={inputCls} data-testid={`cdo-line-variant-${l._k}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Select…</SelectItem>
                        {variants.map((vv) => (<SelectItem key={vv.key} value={vv.key}>{vv.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  {v?.designs?.length > 0 && (
                    <div className="w-36 space-y-1">
                      <Label className={lbl}>Design</Label>
                      <Select value={l.design_id || "__none"} onValueChange={(val) => setLine(l._k, { design_id: val === "__none" ? "" : val })}>
                        <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">—</SelectItem>
                          {v.designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="w-28 space-y-1">
                    <Label className={lbl}>Units</Label>
                    <Input inputMode="numeric" value={l.units} onChange={(e) => onUnitsChange(l, e.target.value)} className={`${inputCls} text-right tabular-nums`} data-testid={`cdo-line-units-${l._k}`} />
                  </div>
                  <div className="w-20 space-y-1">
                    <Label className={lbl}>Pallets</Label>
                    <Input inputMode="decimal" value={l.pallets} onChange={(e) => onPalletsChange(l, e.target.value)} className={`${inputCls} text-right tabular-nums`} data-testid={`cdo-line-pallets-${l._k}`} />
                  </div>
                  <div className="w-32 space-y-1">
                    <Label className={lbl}>Pallet Dims</Label>
                    <Input value={l.pallet_dims} onChange={(e) => setLine(l._k, { pallet_dims: e.target.value })} placeholder="40 × 48 × 50 in" className={inputCls} />
                  </div>
                  <div className="w-24 space-y-1">
                    <div className="flex items-center justify-between gap-1">
                      <Label className={lbl}>Lbs/Pallet</Label>
                      {vByKey[l.key]?.wppCalc && Number(l.weight_per_pallet) === vByKey[l.key].wpp && (
                        <span className="rounded-sm bg-amber-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-700" title="Derived from unit weight × quantity per pallet — no pallet weight entered on this variant" data-testid={`cdo-line-wpp-calc-${l._k}`}>calc</span>
                      )}
                    </div>
                    <Input inputMode="decimal" value={l.weight_per_pallet} onChange={(e) => setLine(l._k, { weight_per_pallet: e.target.value.replace(/[^0-9.]/g, "") })} className={`${inputCls} text-right tabular-nums`} data-testid={`cdo-line-wpp-${l._k}`} />
                  </div>
                  <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-600" onClick={() => setLines((prev) => prev.filter((x) => x._k !== l._k))} data-testid={`cdo-line-remove-${l._k}`}><X className="h-4 w-4" /></Button>
                </div>
              );
            })}
            <button type="button" onClick={() => setLines((prev) => [...prev, emptyLine()])} className="w-full px-3 py-2 text-left text-xs font-semibold text-[#002FA7] hover:bg-slate-50" data-testid="cdo-add-line">+ Add component</button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="space-y-1"><Label className={lbl}>Carrier</Label><Input value={carrier} onChange={(e) => setCarrier(e.target.value)} className={inputCls} data-testid="cdo-carrier" /></div>
            <div className="space-y-1"><Label className={lbl}>Tractor / Truck #</Label><Input value={truckNumber} onChange={(e) => setTruckNumber(e.target.value)} className={inputCls} /></div>
            <div className="space-y-1"><Label className={lbl}>PRO / Tracking #</Label><Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className={inputCls} /></div>
            <div className="space-y-1"><Label className={lbl}>Driver Name</Label><Input value={driverName} onChange={(e) => setDriverName(e.target.value)} className={inputCls} /></div>
            <div className="space-y-1"><Label className={lbl}>SCAC</Label><Input value={scac} onChange={(e) => setScac(e.target.value)} className={inputCls} /></div>
            <div className="space-y-1"><Label className={lbl}>NMFC Class</Label><Input value={nmfcClass} onChange={(e) => setNmfcClass(e.target.value)} placeholder="e.g. 55" className={inputCls} data-testid="cdo-nmfc-class" /></div>
            <div className="space-y-1"><Label className={lbl}>Trailer #</Label><Input value={trailerNumber} onChange={(e) => setTrailerNumber(e.target.value)} className={inputCls} /></div>
            <div className="space-y-1"><Label className={lbl}>Seal #</Label><Input value={sealNumber} onChange={(e) => setSealNumber(e.target.value)} className={inputCls} data-testid="cdo-seal" /></div>
            <div className="space-y-1"><Label className={lbl}>Tracker ID #</Label><Input value={trackerId} onChange={(e) => setTrackerId(e.target.value)} className={inputCls} /></div>
            <div className="space-y-1">
              <Label className={lbl}>Freight Terms</Label>
              <Select value={freightTerms} onValueChange={setFreightTerms}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  <SelectItem value="prepaid">Prepaid</SelectItem>
                  <SelectItem value="collect">Collect</SelectItem>
                  <SelectItem value="third_party">Third Party</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className={lbl}>Physical Pallets</Label><Input inputMode="numeric" value={physicalPallets} onChange={(e) => setPhysicalPallets(e.target.value.replace(/[^0-9]/g, ""))} className={`${inputCls} text-right tabular-nums`} /></div>
          </div>
          <div className="space-y-1">
            <Label className={lbl}>Special Instructions / Shipping Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} data-testid="cdo-notes" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={save} disabled={saving || (partnerAddrs.length > 1 && !shipAddressId)} data-testid="cdo-save">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} {existing ? "Save Changes" : "Create Shipment"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function QuotesDialog({ d, onClose, onChanged }) {
  const [quotes, setQuotes] = useState([]);
  const [carrier, setCarrier] = useState("");
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (d) { setQuotes(d.quotes || []); setCarrier(""); setPrice(""); setNote(""); }
  }, [d]);
  if (!d) return null;
  const bestPrice = Math.min(...quotes.filter((q) => q.price != null).map((q) => q.price));
  const add = async () => {
    if (!carrier.trim()) { toast.error("Enter the carrier name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery-orders/${d.id}/quotes`, { carrier, price: price === "" ? null : parseFloat(price), notes: note });
      setQuotes(data.quotes); setCarrier(""); setPrice(""); setNote("");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to add quote"); }
    finally { setBusy(false); }
  };
  const del = async (qid) => {
    try {
      const { data } = await api.delete(`/delivery-orders/${d.id}/quotes/${qid}`);
      setQuotes(data.quotes); onChanged();
    } catch { toast.error("Failed to delete quote"); }
  };
  const award = async (q) => {
    try {
      const { data } = await api.post(`/delivery-orders/${d.id}/quotes/${q.id}/award`);
      setQuotes(data.quotes);
      toast.success(`${q.carrier} awarded — carrier set on ${d.do_number}`);
      onChanged();
    } catch { toast.error("Failed to award quote"); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm sm:max-w-xl" data-testid="do-quotes-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Carrier Quotes — {d.do_number}</DialogTitle>
          <DialogDescription>Log quote replies from carriers, then award the winner to fill this DO's Carrier field.</DialogDescription>
        </DialogHeader>
        {quotes.length === 0 ? (
          <p className="py-4 text-center text-sm text-slate-500" data-testid="do-quotes-empty">No quotes logged yet.</p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200" data-testid="do-quotes-list">
            {quotes.map((qt) => (
              <div key={qt.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`do-quote-${qt.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-slate-800">{qt.carrier}</span>
                    {qt.awarded && <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`do-quote-awarded-${qt.id}`}>Awarded</span>}
                    {!qt.awarded && qt.price != null && qt.price === bestPrice && quotes.length > 1 && <span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Best price</span>}
                  </div>
                  {qt.notes && <p className="mt-0.5 truncate text-xs text-slate-500">{qt.notes}</p>}
                </div>
                <span className="tabular-nums text-sm font-semibold text-slate-700" data-testid={`do-quote-price-${qt.id}`}>{qt.price != null ? `$${num(qt.price, 2)}` : "—"}</span>
                {qt.awarded ? (
                  <Button variant="outline" size="sm" className="h-7 rounded-sm text-xs" disabled>Awarded</Button>
                ) : (
                  <Button size="sm" className="h-7 rounded-sm bg-[#002FA7] text-xs text-white hover:bg-[#002080]" onClick={() => award(qt)} data-testid={`do-quote-award-${qt.id}`}>Award</Button>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-600" onClick={() => del(qt.id)} data-testid={`do-quote-delete-${qt.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-slate-100 pt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Log a Quote</p>
          <div className="grid grid-cols-[1fr_120px] gap-2">
            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier name" className="rounded-sm" data-testid="do-quote-carrier-input" />
            <Input value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Price $" inputMode="decimal" className="rounded-sm text-right tabular-nums" data-testid="do-quote-price-input" />
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notes (transit time, terms…) — optional" className="mt-2 rounded-sm" data-testid="do-quote-note-input" />
          <div className="mt-3 flex justify-end">
            <Button onClick={add} disabled={busy} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="do-quote-add">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Quote"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DeliveryOrders() {
  const [dos, setDos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [landFreight, setLandFreight] = useState([]);
  const [soRdd, setSoRdd] = useState({});
  const [soPickup, setSoPickup] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [editDo, setEditDo] = useState(null);
  const [cpDialog, setCpDialog] = useState({ open: false, existing: null, dir: "to_copacker" });
  const [fgCp, setFgCp] = useState({ open: false, dir: "to_warehouse" });
  const [components, setComponents] = useState([]);
  const [coPackers, setCoPackers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [delDo, setDelDo] = useState(null);
  const [quotesDo, setQuotesDo] = useState(null);
  const [soList, setSoList] = useState([]);
  const [soPickOpen, setSoPickOpen] = useState(false);
  const [soPickId, setSoPickId] = useState("");
  const [createSo, setCreateSo] = useState(null);
  const [createCustomer, setCreateCustomer] = useState(null);
  const [pickCustomerId, setPickCustomerId] = useState("");
  const companyRef = useRef(null);

  const load = async () => {
    try {
      const [d, c, p, lf, s, comps, cps, sups] = await Promise.all([
        api.get("/delivery-orders"), api.get("/customers"), api.get("/products"), api.get("/land-freight"),
        api.get("/sales-orders").catch(() => ({ data: [] })),
        api.get("/components").catch(() => ({ data: [] })),
        api.get("/co-packers").catch(() => ({ data: [] })),
        api.get("/suppliers").catch(() => ({ data: [] })),
      ]);
      setDos(d.data);
      setCustomers(c.data);
      setProducts(p.data);
      setLandFreight(lf.data);
      setComponents(comps.data);
      setCoPackers(cps.data);
      setSuppliers(sups.data);
      setSoRdd(Object.fromEntries(s.data.map((so) => [so.id, so.ship_date || ""])));
      setSoPickup(Object.fromEntries(s.data.map((so) => [so.id, (so.pickup_delivery || "") === "pickup"])));
      setSoList(s.data.filter((so) => so.status === "open"));
    } catch {
      toast.error("Failed to load delivery orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const getCompany = async () => {
    if (!companyRef.current) {
      try { companyRef.current = (await api.get("/company-settings")).data; } catch { companyRef.current = {}; }
    }
    return companyRef.current;
  };

  // Backfill pallet dims/weights from the current product BOM for DOs created before these fields existed.
  const enrichDo = (d) => {
    const lines = (d.lines || []).map((l) => {
      const cfg = (products.find((p) => p.id === l.product_id)?.pack_configs || []).find((c) => c.id === l.config_id);
      const dims = [cfg?.pallet_length, cfg?.pallet_width, cfg?.pallet_height];
      const dimStr = dims.some((x) => Number(x) > 0) ? `${dims.map((x) => (Number(x) > 0 ? Number(x).toLocaleString() : "?")).join(" × ")} in` : "";
      const wpp = l.weight_per_pallet || Number(cfg?.weight_per_pallet) || null;
      const cpp = l.cases_per_pallet || Number(cfg?.cases_per_pallet) || null;
      const pallets = l.pallets != null ? l.pallets : cpp ? Math.round((l.cases / cpp) * 100) / 100 : null;
      const wpc = l.weight_per_case || Number(cfg?.weight_per_case) || null;
      const weight = wpp && pallets != null ? Math.round(pallets * wpp * 10) / 10
        : wpc ? Math.round(l.cases * wpc * 10) / 10 : l.weight_lbs ?? null;
      return { ...l, pallet_dims: l.pallet_dims || dimStr, weight_per_pallet: wpp, pallets, weight_lbs: weight };
    });
    const computed = Math.round(lines.reduce((s, l) => s + (l.weight_lbs || 0), 0) * 10) / 10;
    return { ...d, lines, rdd: soRdd[d.so_id] || d.rdd || "", computed_weight_lbs: computed, total_pallets: Math.round(lines.reduce((s, l) => s + (l.pallets || 0), 0) * 100) / 100 };
  };

  const resolveAddress = (contact, d) => {
    const addrs = contact?.addresses || [];
    const hit = addrs.find((a) => a.id === d.ship_address_id) || (addrs.length === 1 ? addrs[0] : null);
    return hit ? hit.text : contact?.address || "";
  };

  const shipToParty = (d) => {
    if (d.kind === "fg_copacker") {
      if (d.direction === "to_warehouse") {
        const co = companyRef.current || {};
        return { company_name: co.company_name || "Our Warehouse", address: [co.address_line1, co.address_line2].filter(Boolean).join("\n") };
      }
      const c = customers.find((x) => x.id === d.customer_id) || null;
      return c ? { company_name: c.company_name, address: resolveAddress(c, d), addresses: c.addresses } : { company_name: d.customer_name, address: "" };
    }
    if (d.kind === "component") {
      const list = d.direction === "from_supplier" ? suppliers : coPackers;
      const partner = list.find((p) => p.id === d.partner_id) || null;
      return { company_name: partner?.company_name || d.partner_name, address: partner ? resolveAddress(partner, d) : d.partner_address, addresses: partner?.addresses };
    }
    const c = customers.find((x) => x.id === d.customer_id) || null;
    return c ? { company_name: c.company_name, address: resolveAddress(c, d), addresses: c.addresses } : null;
  };

  const printDO = async (d) => {
    const company = await getCompany();
    printDeliveryOrder(docForPrint(d), company, shipToParty(d));
  };

  const docForPrint = (d) => {
    const e = enrichDo(d);
    if (d.kind === "component" && d.direction === "supplier_to_copacker") {
      const s = suppliers.find((x) => x.id === d.origin_partner_id);
      return { ...e, ship_from_party: { company_name: d.origin_partner_name, address: s?.addresses?.[0]?.text || s?.address || d.origin_partner_address || "" } };
    }
    if (d.kind !== "fg_copacker") return e;
    const p = coPackers.find((x) => x.id === d.partner_id);
    return { ...e, ship_from_party: { company_name: d.partner_name, address: p?.addresses?.[0]?.text || p?.address || d.partner_address || "" } };
  };

  const downloadDO = async (d) => {
    const company = await getCompany();
    try {
      await downloadDeliveryOrderPdf(docForPrint(d), company, shipToParty(d));
      toast.success(`${d.bol_number || d.do_number}.pdf downloaded`);
    } catch {
      toast.error("Failed to generate PDF");
    }
  };

  const shopCarriers = async (d) => {
    const company = await getCompany();
    const e = enrichDo(d);
    const shipTo = shipToParty(d);
    const emails = [...new Set(landFreight.flatMap((f) => (f.contacts || []).filter((c) => !c.suppress_email).map((c) => (c.email || "").trim()).filter(Boolean)))];
    if (!emails.length) toast.warning("No Land Freight contacts with an email found — add them on the Contacts page.");
    const weight = e.total_weight_lbs != null ? e.total_weight_lbs : e.computed_weight_lbs;
    const palletLines = e.lines.map((l) => {
      const bits = [`${num(l.pallets, 2)} pallet${l.pallets === 1 ? "" : "s"}`];
      if (l.pallet_dims) bits.push(l.pallet_dims);
      if (l.weight_per_pallet) bits.push(`${num(l.weight_per_pallet, 1)} lbs/pallet`);
      return bits.join(" — ");
    });
    const body = [
      "Hello Freight Provider,",
      "",
      "Please provide a quote for the following shipment.",
      "",
      "Pickup Location:",
      ...[company.company_name, company.address_line1, company.address_line2].filter(Boolean),
      "",
      "Delivery Location:",
      ...[shipTo?.company_name || d.customer_name, ...String(shipTo?.address || "").split("\n")].filter(Boolean),
      "",
      "Pallet Dimensions and Weights:",
      ...palletLines,
      "",
      `Total Pallets: ${num(e.total_pallets, 2)}`,
      `Total Weight: ${weight ? `${num(weight, 1)} lbs` : "TBD"}`,
      "",
      "Thank you,",
      company.company_name || "",
    ].join("\n");
    const subject = `Freight Quote Request — ${d.do_number} (${num(e.total_pallets, 2)} Pallets${weight ? `, ${num(weight, 1)} lbs` : ""})`;
    window.location.href = `mailto:operations@infinitybrands.com?bcc=${encodeURIComponent(emails.join(","))}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const changeStatus = async (d, status) => {
    try {
      await api.put(`/delivery-orders/${d.id}/status`, { status });
      toast.success(`${d.do_number} → ${doStatusMeta(status, soPickup[d.so_id]).label}`);
      load();
    } catch { toast.error("Failed to update status"); }
  };

  const counts = useMemo(() => {
    const c = { all: dos.length };
    Object.keys(DO_STATUS_META).forEach((k) => { c[k] = dos.filter((d) => d.status === k).length; });
    return c;
  }, [dos]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return dos.filter((d) => {
      if (tab !== "all" && d.status !== tab) return false;
      if (term && ![d.do_number, d.so_number, d.customer_name, d.customer_po, d.carrier, d.tracking_number, ...(d.lines || []).map((l) => l.product_name)].join(" ").toLowerCase().includes(term)) return false;
      return true;
    });
  }, [dos, tab, q]);

  return (
    <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 md:px-10 md:py-12" data-testid="delivery-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Operations</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Shipments</h1>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-shipment">
              <Truck className="mr-1.5 h-4 w-4" /> New Shipment
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 rounded-sm">
            <DropdownMenuItem onClick={() => { setSoPickId(""); setPickCustomerId(""); setSoPickOpen(true); }} data-testid="new-fg-shipment">
              <Boxes className="mr-2 h-4 w-4 text-sky-600" /> Finished Goods: Warehouse → Customer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFgCp({ open: true, dir: "to_customer" })} data-testid="new-fg-copacker-customer-do">
              <Boxes className="mr-2 h-4 w-4 text-emerald-600" /> Finished Goods: Co-Packer → Customer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFgCp({ open: true, dir: "to_warehouse" })} data-testid="new-fg-copacker-do">
              <Factory className="mr-2 h-4 w-4 text-emerald-600" /> Finished Goods: Co-Packer → Warehouse
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCpDialog({ open: true, existing: null, dir: "to_copacker" })} data-testid="new-copacker-shipment">
              <Truck className="mr-2 h-4 w-4 text-violet-600" /> Components: Warehouse → Co-Packer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCpDialog({ open: true, existing: null, dir: "supplier_to_copacker" })} data-testid="new-supplier-copacker-shipment">
              <Truck className="mr-2 h-4 w-4 text-rose-600" /> Components: Supplier → Co-Packer
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCpDialog({ open: true, existing: null, dir: "from_supplier" })} data-testid="new-supplier-warehouse-shipment">
              <Truck className="mr-2 h-4 w-4 text-amber-600" /> Components: Supplier → Warehouse
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="do-tabs">
          {[["all", "All"], ...Object.entries(DO_STATUS_META).map(([k, m]) => [k, m.label])].map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`do-tab-${k}`}>
              {lbl} <span className="ml-1 text-xs font-normal text-slate-400">{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Delivery Orders"
            className="h-9 w-52 rounded-sm border-slate-200 pl-8 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-[#002FA7]" data-testid="do-search" />
          {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-6 flex flex-col items-center border border-dashed border-slate-300 px-6 py-16 text-center" data-testid="do-empty-state">
          <Truck className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">No delivery orders yet.</h2>
          <p className="mt-2 max-w-md text-sm text-slate-500">Create one from any Sales Order — open the <Link to="/sales-orders" className="font-semibold text-[#002FA7] hover:underline">Sales Orders</Link> page and pick <b>Create Delivery Order</b> from the row menu. Partial deliveries are fine.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto border border-slate-200" data-testid="do-table">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                <th className="px-3 py-3">DO #</th>
                <th className="border-l border-slate-200 px-3 py-3">Pickup Date</th>
                <th className="border-l border-slate-200 px-3 py-3">From</th>
                <th className="border-l border-slate-200 px-3 py-3">To</th>
                <th className="border-l border-slate-200 px-3 py-3">Carrier</th>
                <th className="border-l border-slate-200 px-3 py-3">Shipment</th>
                <th className="border-l border-slate-200 px-3 py-3">Status</th>
                <th className="w-28 border-l border-slate-200 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const weight = d.total_weight_lbs != null ? d.total_weight_lbs : d.computed_weight_lbs;
                return (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`do-row-${d.id}`}>
                    <td className="px-3 py-3">
                      <span className="font-semibold text-[#002FA7]" data-testid={`do-number-${d.id}`}>{d.do_number}</span>
                      {d.bol_number && <div className="text-[10px] font-medium text-slate-400" data-testid={`do-bol-number-${d.id}`}>{d.bol_number}</div>}
                      <span className={`mt-1 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${DO_DIRECTION_META[d.direction || "to_customer"]?.cls || ""}`} data-testid={`do-direction-${d.id}`}>
                        {DO_DIRECTION_META[d.direction || "to_customer"]?.label}
                      </span>
                      {d.so_number ? (
                        <div className="text-xs text-slate-400">
                          from <Link to="/sales-orders" className="text-[#002FA7] hover:underline" data-testid={`do-so-link-${d.id}`}>{d.so_number}</Link>
                          {d.customer_po ? ` · PO ${d.customer_po}` : ""}
                        </div>
                      ) : null}
                      {d.po_number ? (
                        <div className="text-xs text-slate-400">
                          for <Link to="/purchase-orders" className="text-[#002FA7] hover:underline" data-testid={`do-po-link-${d.id}`}>{d.po_number}</Link>
                        </div>
                      ) : null}
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 text-slate-600">
                      <div>{fmtLong(d.pickup_date)}</div>
                      {d.status === "completed" && d.delivered_date && <div className="text-xs text-emerald-600">Delivered {fmtLong(d.delivered_date)}</div>}
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 text-slate-600" data-testid={`do-from-${d.id}`}>
                      {d.direction === "supplier_to_copacker" ? d.origin_partner_name
                        : d.kind === "fg_copacker" || d.direction === "from_supplier" ? d.partner_name : "Our Warehouse"}
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 text-slate-800" data-testid={`do-to-${d.id}`}>
                      {d.direction === "from_supplier" || d.direction === "to_warehouse" ? "Our Warehouse"
                        : d.kind === "component" ? d.partner_name
                          : d.customer_name}
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 text-slate-600">
                      <div>{d.carrier || "—"}</div>
                      <div className="text-xs text-slate-400">{[d.truck_number, d.tracking_number].filter(Boolean).join(" · ")}</div>
                      <button type="button" onClick={() => shopCarriers(d)}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
                        title="Draft a freight quote request email to your Land Freight contacts"
                        data-testid={`do-shop-carriers-${d.id}`}>
                        <Mail className="h-3 w-3" /> Shop Carriers
                      </button>
                      <button type="button" onClick={() => setQuotesDo(d)}
                        className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#002FA7] hover:underline"
                        title="Log carrier quote replies and award the winner"
                        data-testid={`do-quotes-${d.id}`}>
                        <DollarSign className="h-3 w-3" /> Quotes{(d.quotes || []).length ? ` (${d.quotes.length})` : ""}
                      </button>
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 text-slate-600" data-testid={`do-shipment-${d.id}`}>
                      <div className="text-xs text-slate-500">{num(d.total_pallets, 2)} Pallet{d.total_pallets === 1 ? "" : "s"}{weight ? ` · ${num(weight, 1)} lbs` : ""}</div>
                      <div>{d.kind === "component" ? `${num(d.total_bottles)} Units` : `${num(d.total_cases, 2)} Cases · ${num(d.total_bottles)} Bottles`}</div>
                      {d.kind === "component" && d.stock_moved && <div className="text-[10px] font-semibold text-violet-600">Stock at co-packer</div>}
                      {d.kind === "fg_copacker" && d.stock_moved && <div className="text-[10px] font-semibold text-emerald-600">FG moved off co-packer</div>}
                      {d.kind === "fg" && d.stock_moved && <div className="text-[10px] font-semibold text-emerald-600">FG shipped from warehouse</div>}
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3">
                      <Select value={d.status} onValueChange={(v) => changeStatus(d, v)}>
                        <SelectTrigger className={`h-7 w-auto gap-1 rounded-sm border-0 px-2 py-0 text-xs font-medium ${doStatusMeta(d.status, soPickup[d.so_id]).cls}`} data-testid={`do-status-${d.id}`}>
                          <span>{doStatusMeta(d.status, soPickup[d.so_id]).label}</span>
                        </SelectTrigger>
                        <SelectContent>
                          {doStatusOptions(!!soPickup[d.so_id]).map(([k, lbl]) => (<SelectItem key={k} value={k}>{lbl}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {d.kind !== "fg_copacker" && <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Edit" onClick={() => (d.kind === "component" ? setCpDialog({ open: true, existing: d, dir: d.direction || "to_copacker" }) : setEditDo(d))} data-testid={`do-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Print Bill of Lading" onClick={() => printDO(d)} data-testid={`do-print-${d.id}`}><Printer className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Download Bill of Lading PDF" onClick={() => downloadDO(d)} data-testid={`do-download-pdf-${d.id}`}><Download className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" title="Delete" onClick={() => setDelDo(d)} data-testid={`do-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DODialog open={!!editDo} onOpenChange={(o) => { if (!o) setEditDo(null); }} existing={editDo} so={null} products={products} customers={customers} onSaved={load} />
      <QuotesDialog d={quotesDo} onClose={() => setQuotesDo(null)} onChanged={load} />
      <ComponentDODialog open={cpDialog.open} onOpenChange={(o) => setCpDialog((s) => ({ ...s, open: o }))} existing={cpDialog.existing} initialDirection={cpDialog.dir} components={components} coPackers={coPackers} suppliers={suppliers} onSaved={load} />
      <CoPackerFGDialog open={fgCp.open} onOpenChange={(o) => setFgCp((s) => ({ ...s, open: o }))} initialDirection={fgCp.dir} coPackers={coPackers} customers={customers} onSaved={load} />
      <Dialog open={soPickOpen} onOpenChange={setSoPickOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="so-pick-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Ship Finished Goods to a Customer</DialogTitle>
            <DialogDescription>Pick the open Sales Order this shipment fulfills — or ship without one.</DialogDescription>
          </DialogHeader>
          <Select value={soPickId || "__none"} onValueChange={(v) => { setSoPickId(v === "__none" ? "" : v); if (v !== "__none") setPickCustomerId(""); }}>
            <SelectTrigger className="rounded-sm" data-testid="so-pick-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Select sales order…</SelectItem>
              {soList.map((s) => (<SelectItem key={s.id} value={s.id}>{s.so_number} — {s.customer_name}{s.ship_date ? ` · RDD ${s.ship_date}` : ""}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> or ship without a sales order <span className="h-px flex-1 bg-slate-200" />
          </div>
          <Select value={pickCustomerId || "__none"} onValueChange={(v) => { setPickCustomerId(v === "__none" ? "" : v); if (v !== "__none") setSoPickId(""); }}>
            <SelectTrigger className="rounded-sm" data-testid="so-pick-customer-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Select customer…</SelectItem>
              {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" className="rounded-sm" onClick={() => setSoPickOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={!soPickId && !pickCustomerId}
              onClick={() => {
                if (soPickId) setCreateSo(soList.find((s) => s.id === soPickId) || null);
                else setCreateCustomer(customers.find((c) => c.id === pickCustomerId) || null);
                setSoPickOpen(false);
              }} data-testid="so-pick-continue">
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <DODialog open={!!createSo} onOpenChange={(o) => { if (!o) setCreateSo(null); }} so={createSo} existing={null} products={products} customers={customers} onSaved={load} />
      <DODialog open={!!createCustomer} onOpenChange={(o) => { if (!o) setCreateCustomer(null); }} so={null} customer={createCustomer} existing={null} products={products} customers={customers} onSaved={load} />

      <AlertDialog open={!!delDo} onOpenChange={(o) => { if (!o) setDelDo(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delDo?.do_number}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the delivery order document. The sales order is not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-red-600 text-white hover:bg-red-700" data-testid="do-delete-confirm"
              onClick={async () => { try { await api.delete(`/delivery-orders/${delDo.id}`); toast.success(`${delDo.do_number} deleted`); setDelDo(null); load(); } catch { toast.error("Failed to delete"); } }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
